// Fetch wrapper type-safe avec validation zod.
// Toute réponse est validée. Si le schéma échoue, on log clair en console et on remonte une erreur.

import { z, type ZodTypeAny } from 'zod';
import { apiBaseUrl, tenant } from './tenant';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details: unknown;

  constructor(message: string, status: number, code: string, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const ApiErrorBodySchema = z.object({
  error: z
    .object({
      message: z.string().optional(),
      code: z.string().optional(),
      details: z.unknown().optional(),
    })
    .optional(),
  message: z.string().optional(),
});

export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined>;
  readonly signal?: AbortSignal;
}

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (!query) return `${base}${cleanPath}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${base}${cleanPath}?${qs}` : `${base}${cleanPath}`;
}

async function parseErrorBody(response: Response): Promise<{ message: string; code: string; details: unknown }> {
  const fallbackMessage = `HTTP ${response.status} ${response.statusText || ''}`.trim();
  try {
    const json: unknown = await response.json();
    const parsed = ApiErrorBodySchema.safeParse(json);
    if (parsed.success) {
      const err = parsed.data.error;
      return {
        message: err?.message ?? parsed.data.message ?? fallbackMessage,
        code: err?.code ?? `HTTP_${response.status}`,
        details: err?.details ?? null,
      };
    }
  } catch {
    /* not json */
  }
  return { message: fallbackMessage, code: `HTTP_${response.status}`, details: null };
}

/**
 * Fait une requête API et valide la réponse contre un schéma zod.
 * @param path Chemin relatif (ex: '/commandes')
 * @param schema Schéma zod pour valider la réponse
 * @param options Options request (method, body, query, signal, headers)
 */
export async function apiRequest<TSchema extends ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> {
  const { method = 'GET', body, query, signal, headers, ...rest } = options;
  const url = buildUrl(path, query);

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Tenant-Id': tenant.id,
    ...(headers as Record<string, string> | undefined),
  };
  let requestBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      requestBody = body;
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(body);
    }
  }

  let response: Response;
  try {
    // `credentials: 'include'` — la session (§4 CONTRACT-AUTHENTIFICATION.md)
    // vit dans un cookie httpOnly `herbo_session` ; sans ceci le cookie ne
    // suit pas la requête si web/API finissent sur des origines différentes
    // en production (en dev le proxy Vite les rend same-origin de toute façon).
    response = await fetch(url, {
      method,
      headers: finalHeaders,
      body: requestBody,
      signal,
      credentials: 'include',
      ...rest,
    });
  } catch (err) {
    throw new ApiError(
      err instanceof Error ? err.message : 'Network error',
      0,
      'NETWORK_ERROR',
      err,
    );
  }

  if (!response.ok) {
    const { message, code, details } = await parseErrorBody(response);
    throw new ApiError(message, response.status, code, details);
  }

  // 204 No Content : on valide undefined
  if (response.status === 204) {
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiError('Réponse vide non attendue', 204, 'EMPTY_RESPONSE', parsed.error);
    }
    return parsed.data as z.infer<TSchema>;
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch (err) {
    throw new ApiError('Réponse non-JSON', response.status, 'INVALID_JSON', err);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    // Log très lisible en console pour debug + erreur typée pour les composants.
    // eslint-disable-next-line no-console
    console.error('[api-client] Validation zod échouée pour', url, parsed.error.format());
    throw new ApiError('Réponse API invalide', response.status, 'SCHEMA_MISMATCH', parsed.error.issues);
  }
  return parsed.data;
}

// Helpers ergonomiques
export const api = {
  get: <S extends ZodTypeAny>(path: string, schema: S, opts?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest(path, schema, { ...opts, method: 'GET' }),
  post: <S extends ZodTypeAny>(path: string, schema: S, body?: unknown, opts?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest(path, schema, { ...opts, method: 'POST', body }),
  put: <S extends ZodTypeAny>(path: string, schema: S, body?: unknown, opts?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest(path, schema, { ...opts, method: 'PUT', body }),
  patch: <S extends ZodTypeAny>(path: string, schema: S, body?: unknown, opts?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest(path, schema, { ...opts, method: 'PATCH', body }),
  delete: <S extends ZodTypeAny>(path: string, schema: S, opts?: Omit<ApiRequestOptions, 'method' | 'body'>) =>
    apiRequest(path, schema, { ...opts, method: 'DELETE' }),
};
